class BenefitsTabComponent extends HTMLElement {
  /** @type {number} */
  #activeIndex = 0;

  connectedCallback() {
    this.#relocateTriggers();
    this.#bindTabs();
    this.#setActive(0, { instant: true });
  }

  #relocateTriggers() {
    const header = this.querySelector('.benefits-tab__nav');
    if (!header) return;

    const wrappers = Array.from(this.querySelectorAll('[data-benefits-tab-trigger-wrapper]'));
    wrappers.forEach((wrapper) => {
      const trigger = wrapper.querySelector('[role="tab"]');
      if (trigger) {
        header.appendChild(trigger);
      }
      wrapper.remove();
    });
  }

  #bindTabs() {
    const tabs = this.#getTabs();

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        this.#setActive(index);
      });

      tab.addEventListener('keydown', (event) => {
        if (!(event instanceof KeyboardEvent)) return;
        let next = index;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          next = (index + 1) % tabs.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          next = (index - 1 + tabs.length) % tabs.length;
        } else if (event.key === 'Home') {
          next = 0;
        } else if (event.key === 'End') {
          next = tabs.length - 1;
        } else {
          return;
        }

        event.preventDefault();
        tabs[next]?.focus();
        this.#setActive(next);
      });
    });
  }

  #getTabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  #getPanels() {
    return Array.from(this.querySelectorAll('[role="tabpanel"]'));
  }

  /**
   * @param {number} index
   * @param {{ instant?: boolean }} [options]
   */
  #setActive(index, options = {}) {
    const tabs = this.#getTabs();
    const panels = this.#getPanels();
    if (!tabs.length || !panels.length) return;

    const safeIndex = Math.max(0, Math.min(index, tabs.length - 1));
    this.#activeIndex = safeIndex;

    tabs.forEach((tab, i) => {
      const isActive = i === safeIndex;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panels.forEach((panel, i) => {
      const isActive = i === safeIndex;
      panel.toggleAttribute('hidden', !isActive);
      panel.toggleAttribute('inert', !isActive);
      this.#syncPanelMedia(panel, isActive);
    });

    if (!options.instant) {
      const panel = panels[safeIndex];
      panel?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  /**
   * @param {HTMLElement} panel
   * @param {boolean} isActive
   */
  #syncPanelMedia(panel, isActive) {
    panel.querySelectorAll('video').forEach((video) => {
      video.controls = false;
      video.muted = true;

      if (isActive) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });

    panel.querySelectorAll('iframe[data-video-type]').forEach((iframe) => {
      if (!(iframe instanceof HTMLIFrameElement)) return;

      const message =
        iframe.dataset.videoType === 'youtube'
          ? isActive
            ? '{"event":"command","func":"playVideo","args":""}'
            : '{"event":"command","func":"pauseVideo","args":""}'
          : isActive
            ? '{"method":"play"}'
            : '{"method":"pause"}';

      iframe.contentWindow?.postMessage(message, '*');
    });
  }

  /**
   * @param {number} index
   * @param {{ instant?: boolean }} [options]
   */
  select(index, options = {}) {
    this.#setActive(index, options);
  }
}

if (!customElements.get('benefits-tab-component')) {
  customElements.define('benefits-tab-component', BenefitsTabComponent);
}
